import axios from 'axios';
import {
  conceptInstanceInterface,
  CotTypesInterface,
  drugConceptsList,
  NDCEditFormDropDownResponseInterface,
  NDCFormFieldsTypes,
  NDCInformationInterface,
  NDCTableInstanceInterfce,
  targetMarketHistoryInstanceInterface,
  targetMarketShareHistoryInterface,
  targetMarketShareInstanceInterface,
  targetMarketStrategyCode,
  tmsHistoricalValueRequestPayloadInterface,
} from 'Interfaces/FactsInterfaces/drugInterfaces';
import {
  ndcHistoryDataInterface,
  ndcHistoryRequestPayloadInterface,
} from 'Interfaces/FactsInterfaces/ndcInterfaces';
import { totalTmsResponseInterface } from 'Interfaces/FactsInterfaces/tmsInterfaces';
import { computed, makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { factScreenServices } from 'services/factsScreenServices';
import { deepCopy } from 'utils/deepCopy';

export class FactsStore {
  // observable class fields

  // Section A observables
  drugSearchInput: string = '';
  allDrugMoleculesDetailsForSearch: conceptInstanceInterface[] = [];
  isAllDrugsLoading = false;
  filteredDrugMoleculesForSearch: string[] = [];

  // Section B & C observables
  drugConceptsList: drugConceptsList = [];
  isConceptsListLoading = false;
  currentTabIndex: number = -1;
  isActivateSelected: boolean = false;
  isInctivateSelected: boolean = false;

  // Section D observables
  marketStrategyTableData: targetMarketShareInstanceInterface[] = [];
  isMarketStrategyTableDataLoading: boolean = false;
  marketStrategyHistoricalData: targetMarketShareHistoryInterface = {
    past_tms_values: [],
    future_tms_values: [],
  };
  // oppositeStrategy historical data stores the data for the reverse strategy like if direct modal is open, it will store indirect, and otherwise
  oppositeStrategyMarketStrategyHistoricalData: targetMarketShareHistoryInterface = {
    past_tms_values: [],
    future_tms_values: [],
  };
  isMarketHistoryLoading: boolean = false;
  marketStrategyModalVisibility: boolean = false;
  indirectMarketStrategyModalVisibility: boolean = false;
  marketStrategyAddModalVisibility: boolean = false;
  isDirectStrategyEditOpen: boolean = false;
  selectedMarketStrategyIndexForEdit: number | null = null;
  selectedCOTMarketStrategyForEdit: CotTypesInterface | null = null;
  isMarketHisotryVisible: boolean = false;
  isMarketHistoryModalVisible: boolean = false;
  isTmsAddModalVisible: boolean = false;
  isTmsViewModalVisible: boolean = false;

  // marketStrategyCode
  targetMarketStrategyCodes: targetMarketStrategyCode[] = [];

  // Section E & F observables
  ndcTablesData: NDCInformationInterface[] = [];
  isNcdTableLoading: boolean = false;
  selectedNdcRowData: NDCInformationInterface | null = null;
  selectedNdcStrategyIndexesForEdit: number[] = [];
  selectedSingleNdcStrategyIndexForEdit: number | null = null;
  isNdcStrategyModalVisible: boolean = false;
  isBulkEditModalVisible: boolean = false;
  selectedNdcSectionContractNumber: string | null = null;
  addModalVisibility: boolean = false;
  selectedContractAccordionIndex: number | null = null;

  ndcFormDropDownData: NDCEditFormDropDownResponseInterface | null = null;
  isNdcHistoryLoading: boolean = false;
  ndcHistoryData: ndcHistoryDataInterface | null = null;
  isNDCAddVisible: boolean = false;
  isNDCViewVisible: boolean = false;
  isTmsCalculationLoading: boolean = false;
  tmsCalculationData: totalTmsResponseInterface | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Section A actions
  handleFetchAllConceptDetailsForSearch = async () => {
    this.isAllDrugsLoading = true;
    try {
      const data = await factScreenServices.getAllMoleculeDetails();
      this.allDrugMoleculesDetailsForSearch = data;
      this.currentTabIndex = 0;
    } catch (_err) {
      toast.error('Could not fetch molecule details, please try again later');
    }
    this.isAllDrugsLoading = false;
  };

  handleFilterDrugSearch = async () => {
    const tempFilteredConcepts: string[] = [];
    for (let i = 0; i < this.allDrugMoleculesDetailsForSearch.length; i++) {
      const moleculeDetails = this.allDrugMoleculesDetailsForSearch[i];
      if (
        moleculeDetails.molecule_dose_form
          .toLocaleLowerCase()
          .search(this.drugSearchInput.toLocaleLowerCase()) != -1
      ) {
        tempFilteredConcepts.push(moleculeDetails.molecule_dose_form);
      }
      if (tempFilteredConcepts.length > 10) break;
    }
    this.filteredDrugMoleculesForSearch = tempFilteredConcepts;
  };

  handleFetchMoleculeAndConceptDetailsByGfc = async (gfc: string) => {
    try {
      this.isAllDrugsLoading = true;
      this.isConceptsListLoading = true;
      const res = await factScreenServices.getConceptsAndMoleculeNamebyGfc(gfc);
      this.drugConceptsList = res.concepts;
      this.drugSearchInput = res.molecule_dose_form;

      this.drugConceptsList.forEach((concept, index) => {
        if (concept.gfc === parseInt(gfc)) {
          this.currentTabIndex = index;
        }
      });
    } catch {
      toast.error('Error occured while fetching the molecule and concept details');
    } finally {
      this.isAllDrugsLoading = false;
      this.isConceptsListLoading = false;
    }
  };

  handleDrugSearchInputChange = (input: string) => {
    this.drugSearchInput = input;
    this.handleFilterDrugSearch();
  };

  toggleActivateSelected = () => {
    this.isActivateSelected = !this.isActivateSelected;
  };

  toggleInActiveSelected = () => {
    this.isInctivateSelected = !this.isInctivateSelected;
  };

  setIsMarketStrategyTableDataLoading = (flag: boolean) => {
    this.isMarketStrategyTableDataLoading = flag;
  };

  handleSearch = async () => {
    if (this.drugSearchInput && !this.isConceptsListLoading) {
      this.isConceptsListLoading = true;
      try {
        const matchedMolecules = this.allDrugMoleculesDetailsForSearch.filter(
          molecule_dose_form => molecule_dose_form.molecule_dose_form === this.drugSearchInput
        );

        if (matchedMolecules.length === 0) {
          // if there are no suggestions, it should no search in that case
          if (this.filteredDrugMoleculesForSearch.length === 0) {
            this.isConceptsListLoading = false;
            toast.error(`'${this.drugSearchInput}' is not a valid Molecule`);
            return;
          }
          this.drugSearchInput = this.filteredDrugMoleculesForSearch[0];
        }

        // reset the data before searching
        this.marketStrategyTableData = [];
        this.tmsCalculationData = null;
        this.selectedContractAccordionIndex = null;
        this.ndcTablesData = [];
        this.currentTabIndex = 0;
        this.isMarketStrategyTableDataLoading = true;
        this.drugConceptsList = await factScreenServices.getParticularMoleculeDetails({
          molecule_dose_form: this.drugSearchInput,
        });
      } catch (_err) {
        console.log({ err: _err });
      }
      this.isConceptsListLoading = false;
    }
  };

  handleEnterKeyDownDrugSearchField = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      this.handleSearch();
    }
  };

  // Section B actions
  setCurrentTabIndex = (newIndex: number) => {
    this.currentTabIndex = newIndex;
    this.selectedContractAccordionIndex = null;
  };

  // Section D actions

  handleFetchTargetMarketShareForSelectedGFC = async (
    signal: AbortController['signal'] | null = null
  ) => {
    try {
      this.isMarketStrategyTableDataLoading = true;
      const gfcString = this.drugConceptsList[this.currentTabIndex].gfc.toString();
      if (gfcString !== '-1') {
        this.marketStrategyTableData = await factScreenServices.getTargetMarketShareByGFC(
          gfcString,
          signal
        );
        this.tmsCalculationData =
          await factScreenServices.getTotalTmsCalculationForDirectIndirectandOverall(
            gfcString,
            signal
          );
      }
      this.isMarketStrategyTableDataLoading = false;
    } catch (_error) {
      if (!axios.isCancel(_error)) {
        toast('Some Error occured while fetching target market share, please try again later');
        this.isNcdTableLoading = false;
      }
    } finally {
      // this.isMarketStrategyTableDataLoading = false;
    }
  };

  handleFetchTargetMarketShareForGfc = async (gfc: string) => {
    try {
      this.isMarketStrategyTableDataLoading = true;
      this.isTmsCalculationLoading = true;
      if (gfc) {
        const data = await Promise.all([
          factScreenServices.getTargetMarketShareByGFC(gfc),
          factScreenServices.getTotalTmsCalculationForDirectIndirectandOverall(gfc),
        ]);
        this.marketStrategyTableData = data[0];
        this.tmsCalculationData = data[1];
      }
    } catch (_err) {
      console.log('Error occured while fetching target market share');
    } finally {
      this.isMarketStrategyTableDataLoading = false;
      this.isTmsCalculationLoading = false;
    }
  };

  handleFetchMarketHitoricalData = async () => {
    this.isMarketHistoryLoading = true;
    try {
      const _tableData = this.tableDataForEditModal();
      const payload: tmsHistoricalValueRequestPayloadInterface = {
        GFC: this.drugConceptsList[this.currentTabIndex].gfc.toString(),
        COT: this.selectedCOTMarketStrategyForEdit as string,
        SellingCategory: this.isDirectStrategyEditOpen ? 'Direct' : 'Indirect',
      };
      const res = await factScreenServices.getMarketShareHistoryValues(payload);
      if (this.isDirectStrategyEditOpen) {
        this.marketStrategyHistoricalData = res.Direct;
        this.oppositeStrategyMarketStrategyHistoricalData = res.Indirect;
      } else {
        this.marketStrategyHistoricalData = res.Indirect;
        this.oppositeStrategyMarketStrategyHistoricalData = res.Direct;
      }
      this.isMarketHistoryLoading = false;
      this.isMarketHistoryLoading = false;
    } catch (_err) {
      console.log('Error occured while getting data for TMS historical values');
    } finally {
      this.isMarketHistoryLoading = false;
    }
  };

  handleFetchMarketHitoricalDataForCot = async (
    COT: string,
    SellingCategory: 'Direct' | 'Indirect'
  ) => {
    this.isMarketHistoryLoading = true;
    try {
      const payload: tmsHistoricalValueRequestPayloadInterface = {
        GFC: this.drugConceptsList[this.currentTabIndex].gfc.toString(),
        COT: COT,
        SellingCategory: SellingCategory,
      };

      // check which one to be used from the response on the basis of selling category
      if (SellingCategory === 'Direct') {
        this.marketStrategyHistoricalData = (
          await factScreenServices.getMarketShareHistoryValues(payload)
        ).Direct;
      } else {
        this.marketStrategyHistoricalData = (
          await factScreenServices.getMarketShareHistoryValues(payload)
        ).Indirect;
      }

      this.isMarketHistoryLoading = false;
      this.isMarketHistoryLoading = false;
    } catch (_err) {
      console.log('Error occured while getting data for TMS historical values');
    } finally {
      this.isMarketHistoryLoading = false;
    }
  };

  setMarketHistoricalFutureDataForCot = (
    future_tms_values: targetMarketHistoryInstanceInterface[]
  ) => {
    this.marketStrategyHistoricalData.future_tms_values = future_tms_values;
  };

  deleteParticularIndexFutureTms = (marketShareId: number) => {
    const temp: targetMarketShareHistoryInterface = {
      past_tms_values: this.marketStrategyHistoricalData.past_tms_values,
      future_tms_values: [],
    };
    this.marketStrategyHistoricalData.future_tms_values.forEach(futureInstance => {
      if (futureInstance.MarketShareID !== marketShareId) {
        temp.future_tms_values.push(futureInstance);
      }
    });

    this.marketStrategyHistoricalData = temp;
  };

  resetHistoricalData = () => {
    this.marketStrategyHistoricalData = {
      past_tms_values: [],
      future_tms_values: [],
    };
  };

  setIsDirectStrategyEditOpen = (isDirectEditOpen: boolean) => {
    this.isDirectStrategyEditOpen = isDirectEditOpen;
  };

  toggleMarketStrategyModalVisibility = () => {
    this.marketStrategyModalVisibility = !this.marketStrategyModalVisibility;
  };

  toggleTMSAddVisibility = () => {
    this.isTmsAddModalVisible = !this.isTmsAddModalVisible;
  };

  toggleTMSViewVisibility = () => {
    this.isTmsViewModalVisible = !this.isTmsViewModalVisible;
  };

  toggleIndirectMarketStrategyModalVisibility = () => {
    this.indirectMarketStrategyModalVisibility = !this.indirectMarketStrategyModalVisibility;
  };

  toggleMarketStrategyAddModalVisibility = () => {
    this.marketStrategyAddModalVisibility = !this.marketStrategyAddModalVisibility;
  };

  toggleAddModalVisibility = () => {
    this.addModalVisibility = !this.addModalVisibility;
  };

  setSelectedMarketStrategyIndexForEdit = (
    index: number,
    isDirectSelected: boolean,
    cotType: CotTypesInterface
  ) => {
    this.selectedMarketStrategyIndexForEdit = index;
    this.selectedCOTMarketStrategyForEdit = cotType;
    this.marketStrategyModalVisibility = true;
    this.isDirectStrategyEditOpen = isDirectSelected;
    this.handleFetchMarketHitoricalData();
  };
  setSelectedMarketStrategyIndexForAdd = (
    index: number,
    isDirectSelected: boolean,
    cotType: CotTypesInterface
  ) => {
    this.selectedMarketStrategyIndexForEdit = index;
    this.selectedCOTMarketStrategyForEdit = cotType;
    this.isTmsAddModalVisible = true;
    this.isDirectStrategyEditOpen = isDirectSelected;
    this.handleFetchMarketHitoricalData();
  };
  setSelectedMarketStrategyIndexForView = (
    index: number,
    isDirectSelected: boolean,
    cotType: CotTypesInterface
  ) => {
    this.selectedMarketStrategyIndexForEdit = index;
    this.selectedCOTMarketStrategyForEdit = cotType;
    this.isTmsViewModalVisible = true;
    this.isDirectStrategyEditOpen = isDirectSelected;
    this.handleFetchMarketHitoricalData();
  };
  toggleMarketHitoryVisibility = () => {
    this.isMarketHisotryVisible = !this.isMarketHisotryVisible;
  };
  @computed directMarketStrategyTableData = () => {
    const temp = this.marketStrategyTableData.filter(
      rowData => rowData.SellingCategory === 'Direct'
    );
    return temp;
  };
  @computed indirectMarketStrategyTableData = () => {
    const temp = this.marketStrategyTableData.filter(
      rowData => rowData.SellingCategory !== 'Direct'
    );
    return temp;
  };

  @computed tableDataForEditModal = () => {
    if (this.isDirectStrategyEditOpen) {
      let tableData = this.directMarketStrategyTableData()[0];
      this.directMarketStrategyTableData().forEach(strategyData => {
        if (strategyData.COT === this.selectedCOTMarketStrategyForEdit) {
          tableData = strategyData;
        }
      });
      return tableData;
    } else {
      let tableData = this.indirectMarketStrategyTableData()[0];
      this.indirectMarketStrategyTableData().forEach(strategyData => {
        if (strategyData.COT === this.selectedCOTMarketStrategyForEdit) {
          tableData = strategyData;
        }
      });
      return tableData;
    }
  };

  fetchAllTargetMarketStrategyCodes = async () => {
    this.targetMarketStrategyCodes = await factScreenServices.getTargetMarketStrategyCodes();
  };

  @computed targetMarketStrategyCodeOptions = () => {
    const temp: { name: string; code: string }[] = [];
    this.targetMarketStrategyCodes.forEach(strategyCode => {
      temp.push({
        code: strategyCode.TMSStrategyID.toString(),
        name: strategyCode.TMSStrategyCode,
      });
    });
    return temp;
  };

  /**
   * Section E
   * Actions
   */

  setSelectedContractAccordionIndex = (index: number) => {
    this.selectedContractAccordionIndex = index;
  };

  setIsNdcTableLoading = (flag: boolean) => {
    this.isNcdTableLoading = flag;
  };

  handleFetchNDCInfo = async (_gfc = '', signal: AbortController['signal'] | null = null) => {
    this.isNcdTableLoading = true;
    this.selectedNdcSectionContractNumber = '';
    this.selectedNdcStrategyIndexesForEdit = [];
    try {
      const gfcString = this.drugConceptsList[this.currentTabIndex].gfc.toString();
      if (gfcString !== '-1') {
        this.ndcTablesData = await factScreenServices.getNdcInformationByGFC(gfcString, signal);
      }
      this.isNcdTableLoading = false;
    } catch (_error) {
      if (!axios.isCancel(_error)) {
        toast('Some error occured while fetching the ndc information, please try again later');
        this.isNcdTableLoading = false;
      }
    } finally {
      // intentionally empty
    }
  };

  handleFetchNDCInfoByGivenGfc = async (gfc: string) => {
    this.isNcdTableLoading = true;
    this.selectedNdcSectionContractNumber = '';
    this.selectedNdcStrategyIndexesForEdit = [];
    try {
      this.ndcTablesData = await factScreenServices.getNdcInformationByGFC(gfc);
    } catch {
      console.log('Error occureing while fetching the ndc information');
    } finally {
      this.isNcdTableLoading = false;
    }
  };

  handleFetchNdcHistory = async () => {
    try {
      this.isNdcHistoryLoading = true;
      const payload: ndcHistoryRequestPayloadInterface = {
        NDC11: [this.selectedNdcRowData?.NDC11 as string],
      };
      this.ndcHistoryData = await factScreenServices.getNdcHistory(payload);
    } catch (_err) {
      console.log('Failed to load ndc history');
    } finally {
      this.isNdcHistoryLoading = false;
    }
  };

  setNdcHistoryData = (ndcHistoryFutureData: ndcHistoryDataInterface) => {
    this.ndcHistoryData = ndcHistoryFutureData;
  };

  deleteParticularIndexFutureNDC = (id: number, strategy: NDCFormFieldsTypes) => {
    if (this.ndcHistoryData) {
      const tempNdcHistoryData = deepCopy(this.ndcHistoryData);
      if (strategy === 'PPS') {
        tempNdcHistoryData.PPS_History.future = tempNdcHistoryData?.PPS_History.future.filter(
          instance => instance.ID != id
        );
      } else if (strategy === 'PPSSC') {
        tempNdcHistoryData.PPS_Reason_History.future =
          tempNdcHistoryData.PPS_Reason_History.future.filter(instance => instance.ID != id);
      } else if (strategy === 'Segmentation') {
        tempNdcHistoryData.Segmentation.future = tempNdcHistoryData.Segmentation.future.filter(
          instance => instance.ComponentID != id
        );
      } else if (strategy === 'DPStrategy') {
        tempNdcHistoryData.dp_strategy.future = tempNdcHistoryData.dp_strategy.future.filter(
          instance => instance.ComponentID != id
        );
      }
      this.ndcHistoryData = tempNdcHistoryData;
    }
  };

  handleFetchNdcEditDropDownData = async () => {
    this.ndcFormDropDownData = await factScreenServices.getNdcEditFormFields();
  };

  handleSetSelectedSectionContractNumber = async (contractNumber: string) => {
    this.selectedNdcSectionContractNumber = contractNumber;
  };

  @computed ndcSections = () => {
    const ndcSectionsData: Map<string, NDCTableInstanceInterfce> = new Map();
    this.ndcTablesData.map(row => {
      if (ndcSectionsData.has(row.ContractNumber)) {
        const tempRowData = ndcSectionsData.get(row.ContractNumber)?.RowData;
        const tempNdcSection = {
          ContractType: row.ContractType,
          ContractNumber: row.ContractNumber,
          ContractStartDate: row.ContractStartDate,
          ContractEndDate: row.ContractEndDate,
          ReportingGroup: row.ReportingGroup,
          RowData: [...(tempRowData || []), row],
        };
        ndcSectionsData.set(row.ContractNumber, tempNdcSection);
      } else {
        const tempNdcSection = {
          ContractType: row.ContractType,
          ContractNumber: row.ContractNumber,
          ContractStartDate: row.ContractStartDate,
          ContractEndDate: row.ContractEndDate,
          ReportingGroup: row.ReportingGroup,
          RowData: [row],
        };
        ndcSectionsData.set(row.ContractNumber, tempNdcSection);
      }
    });

    const tempResponse: NDCTableInstanceInterfce[] = [];
    ndcSectionsData.forEach((key, _value) => {
      tempResponse.push(key);
    });
    tempResponse.sort((a, b) => a.ContractNumber.localeCompare(b.ContractNumber));
    return tempResponse;
  };

  @computed rowDataForSelectedNdcSectionByContractNumber = (index: number) => {
    const ndcSections = this.ndcSections();
    let tempRow: NDCInformationInterface = ndcSections[0].RowData[0];

    // const tempSection =
    // return
    ndcSections.forEach(section => {
      if (section.ContractNumber === this.selectedNdcSectionContractNumber) {
        tempRow = section.RowData[index];
      }
    });
    return tempRow;
  };

  handleEditSingleNdcStrategy = (index: number, rowData: NDCInformationInterface) => {
    this.selectedNdcRowData = rowData;
    this.selectedNdcStrategyIndexesForEdit = [];
    this.selectedSingleNdcStrategyIndexForEdit = index;
    this.isNdcStrategyModalVisible = true;
  };
  handleViewSingleNdcStrategy = (index: number, rowData: NDCInformationInterface) => {
    this.selectedNdcRowData = rowData;
    this.selectedNdcStrategyIndexesForEdit = [];
    this.selectedSingleNdcStrategyIndexForEdit = index;
    this.isNDCViewVisible = true;
  };
  handleAddSingleNdcStrategy = (index: number, rowData: NDCInformationInterface) => {
    this.selectedNdcRowData = rowData;
    this.selectedNdcStrategyIndexesForEdit = [];
    this.selectedSingleNdcStrategyIndexForEdit = index;
    this.isNDCAddVisible = true;
  };
  handleNdcBultEdit = (_index: number) => {
    this.isBulkEditModalVisible = true;
  };

  resetNdcSelectedIndexes = () => {
    this.selectedNdcStrategyIndexesForEdit = [];
    this.selectedSingleNdcStrategyIndexForEdit = null;
  };

  handleSetNdcStrategyIndexes = (indexes: number[]) => {
    this.selectedNdcStrategyIndexesForEdit = indexes;
  };

  addNdcIndexForMultiEdit = (index: number, contractNumber: string) => {
    if (this.selectedNdcSectionContractNumber !== contractNumber) {
      this.selectedNdcSectionContractNumber = contractNumber;
      this.selectedNdcStrategyIndexesForEdit = [index];
    } else {
      if (!this.checkIfNdcIndexIsSelected(index, contractNumber)) {
        this.selectedNdcStrategyIndexesForEdit = [...this.selectedNdcStrategyIndexesForEdit, index];
        this.selectedSingleNdcStrategyIndexForEdit = null;
      } else {
        const ind = this.selectedNdcStrategyIndexesForEdit.indexOf(index);
        this.selectedNdcStrategyIndexesForEdit.splice(ind, 1);
      }
    }
  };

  handleUpdateNdcHistoryData = (newHistoricalData: ndcHistoryDataInterface) => {
    this.ndcHistoryData = newHistoricalData;
  };

  checkIfNdcIndexIsSelected = (index: number, contractNumber: string) => {
    if (this.selectedNdcSectionContractNumber !== contractNumber) {
      return false;
    }

    const ind = this.selectedNdcStrategyIndexesForEdit.indexOf(index);
    if (ind !== -1) return true;
    return false;
  };

  toggleNdcStrategyModalVisibility = () => {
    if (this.isNdcStrategyModalVisible) {
      this.selectedNdcStrategyIndexesForEdit = [];
      this.selectedSingleNdcStrategyIndexForEdit = null;
      this.isNdcStrategyModalVisible = false;
    } else {
      this.isNdcStrategyModalVisible = true;
    }
  };

  toggleNdcBulkEditStrategyModal = () => {
    if (this.isBulkEditModalVisible) {
      this.isBulkEditModalVisible = false;
      this.selectedNdcStrategyIndexesForEdit = [];
    } else {
      this.isBulkEditModalVisible = true;
    }
  };

  toggleNDCViewModalVisibility = () => {
    this.isNDCViewVisible = !this.isNDCViewVisible;
  };

  toggleNDCAddModalVisibility = () => {
    this.isNDCAddVisible = !this.isNDCAddVisible;
  };

  toggleMarketHistoryModalVisibility = () => {
    this.isMarketHistoryModalVisible = !this.isMarketHistoryModalVisible;
  };

  resetStore = () => {
    // Section A observables
    this.drugSearchInput = '';
    this.allDrugMoleculesDetailsForSearch = [];
    this.isAllDrugsLoading = false;
    this.filteredDrugMoleculesForSearch = [];

    // Section B & C observables
    this.drugConceptsList = [];
    this.isConceptsListLoading = false;
    this.currentTabIndex = -1;
    this.isActivateSelected = false;
    this.isInctivateSelected = false;

    // Section D observables
    this.marketStrategyTableData = [];
    this.isMarketStrategyTableDataLoading = false;
    this.marketStrategyHistoricalData = {
      past_tms_values: [],
      future_tms_values: [],
    };
    this.oppositeStrategyMarketStrategyHistoricalData = {
      past_tms_values: [],
      future_tms_values: [],
    };
    this.isMarketHistoryLoading = false;
    this.marketStrategyModalVisibility = false;
    this.indirectMarketStrategyModalVisibility = false;
    this.marketStrategyAddModalVisibility = false;
    this.isDirectStrategyEditOpen = false;
    this.selectedMarketStrategyIndexForEdit = null;
    this.selectedCOTMarketStrategyForEdit = null;
    this.isMarketHisotryVisible = false;
    this.isMarketHistoryModalVisible = false;
    this.isTmsAddModalVisible = false;
    this.isTmsViewModalVisible = false;

    // marketStrategyCode
    this.targetMarketStrategyCodes = [];

    // Section E & F observables
    this.ndcTablesData = [];
    this.isNcdTableLoading = false;
    this.selectedNdcRowData = null;
    this.selectedNdcStrategyIndexesForEdit = [];
    this.selectedSingleNdcStrategyIndexForEdit = null;
    this.isNdcStrategyModalVisible = false;
    this.isBulkEditModalVisible = false;
    this.selectedNdcSectionContractNumber = null;
    this.addModalVisibility = false;
    this.selectedContractAccordionIndex = null;

    this.ndcFormDropDownData = null;
    this.isNdcHistoryLoading = false;
    this.ndcHistoryData = null;
    this.isNDCAddVisible = false;
    this.isNDCViewVisible = false;
    this.isTmsCalculationLoading = false;
    this.tmsCalculationData = null;
  };
}
